import type { ViewSettingsAdapter } from "@auger/ui/composables/viewSettings/adapter"
import type { ViewSettingEntry } from "@auger/ui/composables/viewSettings/entry"
import {
  appendUserViewSetting,
  deleteUserViewSettingKey,
  ensureUserDataRow,
} from "@/common/hasura/userdata"
import { client as apolloClient } from "@/init/apollo"
import { useUserStore } from "@/stores/user"

// Browsers cap the bodies of in-flight keepalive requests at 64KB in total;
// an entry near that goes out as an ordinary request instead.
const KEEPALIVE_MAX_BYTES = 32_000

/**
 * Settings writes are sent `keepalive`, so they outlive the page: a reload or
 * a closed tab right after a change neither aborts the write mid-flight (a
 * "Failed to fetch" network error) nor loses the change.
 */
const KEEPALIVE = { fetchOptions: { keepalive: true } }
function writeContext(variables: object) {
  return JSON.stringify(variables).length > KEEPALIVE_MAX_BYTES ? undefined : KEEPALIVE
}

/**
 * The frontend's view settings: `user_data.view_settings` (a JSONB object keyed
 * by view: `layout:record:{type}`, `grid:{key}`, ...), read through the Pinia
 * user store, which is populated at login so views never fetch.
 *
 * Writes touch only their own key via jsonb `_append`/`_delete_key`, never the
 * whole column — two views (or two tabs) saving different keys can never
 * clobber each other's write, only their own. The policy around them (when to
 * write, debouncing, which copy wins) is @auger/ui's useViewSettings.
 *
 * `client` overrides the shared Apollo client. Tests only.
 */
export function createUserViewSettingsAdapter({
  client = apolloClient,
}: { client?: typeof apolloClient } = {}): ViewSettingsAdapter {
  /** Append one key. If no user_data row exists yet, create one and retry. Throws on failure. */
  async function persistEntry(key: string, entry: ViewSettingEntry): Promise<void> {
    const variables = { entry: { [key]: entry } }
    const append = () =>
      client.mutate({
        mutation: appendUserViewSetting,
        variables,
        context: writeContext(variables),
      })
    const result = await append()
    if ((result.data?.update_user_data?.affected_rows ?? 0) > 0) return
    // No user_data row yet — _append only matches existing rows. ensureUserDataRow's
    // on_conflict is a no-op (update_columns: []), so it never overwrites
    // view_settings even if a concurrent writer's insert — for a *different*
    // key — created the row a moment earlier; retrying the append afterward
    // is what actually merges our entry in, whichever writer's insert won.
    await client.mutate({ mutation: ensureUserDataRow, context: KEEPALIVE })
    const retried = await append()
    if ((retried.data?.update_user_data?.affected_rows ?? 0) === 0) {
      // Row creation and the append both reported success but nothing
      // actually matched — something's wrong (e.g. permissions). Throw
      // rather than let the caller believe this entry is on the server.
      throw new Error(`append matched no row for ${key} even after ensuring one exists`)
    }
  }

  return {
    get: (key) => useUserStore().viewSettings[key] as ViewSettingEntry | undefined,
    set: (key, entry) => useUserStore().setViewSetting(key, entry),
    remove: (key) => useUserStore().removeViewSetting(key),
    write: persistEntry,
    erase: (key) =>
      client
        .mutate({
          mutation: deleteUserViewSettingKey,
          variables: { key },
          context: KEEPALIVE,
        })
        .then(() => undefined),
  }
}
